<template>
  <div v-if="saveBroadcastSearchSteps && saveBroadcastSearchSteps.length">
    <h1 class="text-2xl sm:text-3xl font-bold text-center mt-6 sm:mt-0">
      How to save broadcast search on www.TheBrokerSite.com
    </h1>

    <p class="mb-10 mt-5 text-gray-600 text-center max-w-3xl mx-auto text-sm sm:text-base">
      Saving a broadcast search lets you automatically track relevant want-to-buy and want-to-sell opportunities without manually checking listings. 
      Your saved criteria are used to match against new broadcasts as they are posted. You can choose how you want to stay updated—either through instant email alerts or a daily digest summarizing all matched opportunities.
    </p>

    <div class="space-y-8">
      <div v-for="(step, index) in saveBroadcastSearchSteps" :key="index" class="bg-white p-6 rounded-lg shadow-md">
        <!-- CONTENT -->
        <div class="flex flex-col lg:flex-row items-center gap-8">
          <!-- TEXT (2/5) -->
          <div class="w-full lg:w-2/6">
            <div v-if="step.thumbnail" class=" rounded-xl flex items-center justify-center p-3 bg-white ">
              <img :src="step.thumbnail" class="w-24 object-contain " alt="Step 1 Thumbnail">
            </div>
            <h2 class="text-xl font-bold text-red-700 mb-2 text-center">
              {{ step.title }}
            </h2>
            <p class="text-gray-600 text-sm px-4 text-center leading-5">
              {{ step.description }}
            </p>
          </div>

          <!-- IMAGE (3/5) -->
          <div class="w-full lg:w-4/6 flex justify-center">
            <a :href="step.image" target="_blank" rel="noopener noreferrer">
              <img :src="step.image" :alt="step.title" class="rounded-xl shadow-md border w-full cursor-pointer" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <template>
      <FAQSection :faqs="faqs" />
    </template>
  </div>
</template>

<script>

import FAQSection from '@/components/FAQSection.vue';
import { useSchemaOrg } from '../composables/useSchema.org.js';
import { mapState } from 'vuex';


export default {
  name: 'HowtoSaveBroadcastSearch',
  components: {
    FAQSection
  },
  data: () => ({
    saveBroadcastSearchSteps: [
      {
        title: "Enter keywords and filters",
        description: "Start by typing a keyword related to the broadcast you want to track. You can refine your search using available filters such as broadcast list type (PC, Laptop, Components, and more), broadcast type (All, WTB, WTS), and Country or Region to narrow down relevant results.",
        image: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-1.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-1-thumbnail.webp'
      },
      {
        title: "Run your broadcast search",
        description: `Click the "Search" button to find matching broadcasts. The system will combine your keyword and selected filters to display the most relevant want-to-buy and want-to-sell listings available.`,
        image: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-2.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-2-thumbnail.webp'
      },
      {
        title: "Choose notifications",
        description: "Click Save to store your search criteria. You can choose how you want to receive updates: either instant email notifications when new matches are found, or a daily email digest summarizing all matched broadcasts.",
        image: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-3.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-3-thumbnail.webp'
      },
      {
        title: "View your saved searches",
        description: "After saving, your search will appear in the Saved Searches list where you can manage, edit, or remove it anytime. This helps you easily track all active search alerts in one place.",
        image: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-4.webp',
        thumbnail: 'https://www.thebrokersite.com/images/help/save_broadcast_search/save-broadcast-search-step-4-thumbnail.webp'
      },
    ]
  }),
  mounted() {
    const helpSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to save broadcast search on www.TheBrokerSite.com",
      description: `Saving a broadcast search lets you automatically track relevant want-to-buy and want-to-sell opportunities without manually checking listings. 
      Your saved criteria are used to match against new broadcasts as they are posted. You can choose how you want to stay updated—either through instant email alerts or a daily digest summarizing all matched opportunities.`,
      image: this.saveBroadcastSearchSteps.map(step => step.image),

      step: this.saveBroadcastSearchSteps.map((step, index) => ({
        "@type": "HowToStep",
        name: step.title,
        text: step.description,
        image: step.image,
        position: index + 1
      })),

      mainEntity: {
        "@type": "FAQPage",
        mainEntity: this.faqs.map(faq => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer
          }
        }))
      }
    };
    useSchemaOrg(helpSchema);
  },
  computed: {
    ...mapState(['frequentlyAskedQuestions']),
    faqs() {
      return this.frequentlyAskedQuestions['how_to_save_broadcast_search']
    }
  }
}
</script>
